import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowUpRight, Newspaper, ShieldCheck, Users, Handshake, Vote } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionHead } from "./PageHero";
import { OBJECTIVES, SECTORS } from "../../data/content";
import { api, fileUrl, formatDate } from "../../lib/api";

export const ObjectivesGrid = () => (
  <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {OBJECTIVES.map(({ icon: Icon, title, text }, i) => (
      <Reveal key={title} delay={i * 0.06}>
        <div className="card-lift group h-full rounded-xl border border-slate-200 bg-white p-7" data-testid={`objective-card-${i}`}>
          <div className="flex items-start justify-between">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-royal-light text-royal transition-colors duration-300 group-hover:bg-royal group-hover:text-white">
              <Icon className="h-6 w-6" />
            </span>
            <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
          </div>
          <h3 className="mt-6 font-display text-lg font-bold text-navy">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const SectorsGrid = ({ dark = false }) => (
  <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {SECTORS.map(({ icon: Icon, label }, i) => (
      <Reveal key={label} delay={i * 0.03}>
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3.5 transition-colors duration-200 ${dark ? "border-white/10 bg-white/5 text-white hover:bg-white/10" : "border-slate-200 bg-white text-navy hover:border-leaf/40"}`} data-testid={`sector-${i}`}>
          <Icon className={`h-5 w-5 shrink-0 ${dark ? "text-emerald-300" : "text-leaf"}`} />
          <span className="text-sm font-semibold">{label}</span>
        </div>
      </Reveal>
    ))}
  </div>
);

const AFF_POINTS = [
  { icon: Users, title: "Organised labour representation", text: "A recognised platform to raise the collective concerns of IT employees." },
  { icon: Handshake, title: "Collective bargaining", text: "Strength in numbers when engaging with employers on fair terms." },
  { icon: ShieldCheck, title: "Employee protection", text: "Support from an established trade union network when employees need it." },
  { icon: Vote, title: "Democratic participation", text: "Members take part in a democratic, member-driven trade union process." },
];

export const AffiliateLogo = ({ src, label, caption, testId }) => (
  <figure className="flex w-32 shrink-0 flex-col items-center" data-testid={testId}>
    <div className="flex h-32 w-32 items-center justify-center rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <img src={src} alt={`${label} logo`} className="max-h-full max-w-full object-contain" />
    </div>
    <figcaption className="mt-2 text-center text-[11px] font-semibold leading-tight text-slate-600">{caption}</figcaption>
  </figure>
);

export const AffiliationBlock = () => (
  <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
    <Reveal className="lg:col-span-5">
      <div className="h-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="affiliation-card">
        <span className="eyebrow">Our Affiliation</span>
        <h3 className="mt-4 font-display text-2xl font-extrabold text-navy">Affiliated to STU – Swatantra Thozhilali Union</h3>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">
          STU is a registered trade union associated with the Indian Union Muslim League (IUML). Through this affiliation, the IT Employees Federation is part of an organised labour movement committed to the rights and welfare of working people.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <AffiliateLogo src="/images/stu-logo.jpg" label="STU" caption="Swatantra Thozhilali Union" testId="stu-logo" />
          <AffiliateLogo src="/images/iuml-logo.jpg" label="IUML" caption="Indian Union Muslim League" testId="iuml-logo" />
        </div>
      </div>
    </Reveal>
    <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
      {AFF_POINTS.map(({ icon: Icon, title, text }, i) => (
        <Reveal key={title} delay={i * 0.08}>
          <div className="h-full rounded-xl border-l-4 border-leaf bg-white p-6 shadow-sm">
            <Icon className="h-6 w-6 text-royal" />
            <h4 className="mt-4 font-display font-bold text-navy">{title}</h4>
            <p className="mt-2 text-sm text-slate-600">{text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

export const NewsCard = ({ item, i }) => (
  <Link to={`/news/${item.slug}`} className="card-lift group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`news-card-${i}`}>
    <div className="relative aspect-[16/9] overflow-hidden bg-navy">
      {item.image_file_id ? (
        <img src={fileUrl(item.image_file_id)} alt={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full items-center justify-center grid-texture"><Newspaper className="h-10 w-10 text-emerald-300/70" /></div>
      )}
      <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold text-royal">{item.category}</span>
    </div>
    <div className="flex flex-1 flex-col p-6">
      <p className="text-xs text-slate-500">{formatDate(item.published_at)}</p>
      <h3 className="mt-2 font-display text-lg font-bold leading-snug text-navy group-hover:text-royal">{item.title}</h3>
      <p className="mt-2 line-clamp-3 text-sm text-slate-600">{item.summary}</p>
      <span className="mt-auto pt-5 inline-flex items-center gap-1 text-sm font-semibold text-royal">Read more <ArrowUpRight className="h-4 w-4" /></span>
    </div>
  </Link>
);

export const LatestNews = () => {
  const { data = [] } = useQuery({ queryKey: ["news"], queryFn: () => api.get("/news").then((r) => r.data) });
  return (
    <section className="py-16 md:py-24" data-testid="latest-news-section">
      <div className="container-x">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHead eyebrow="News & Activities" title="Latest from the federation" />
          <Link to="/news" className="btn-outline self-start" data-testid="view-all-news">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {data.slice(0, 3).map((n, i) => <Reveal key={n.id} delay={i * 0.08}><NewsCard item={n} i={i} /></Reveal>)}
        </div>
      </div>
    </section>
  );
};
